import React, { useState } from 'react';
import { TimeSlot, Person } from '../graph/types';
import { formatTimeSlotsForEmail, getTimezoneAbbreviation, getTimezoneFullName } from '../logic/availabilityLogic';
import { formatTime, formatDateLabel } from '../utils/dateUtils';

interface ResultsListProps {
  slots: TimeSlot[];
  timeZone: string;
  attendees: Person[];
  includeAttendees: boolean;
  onInsertIntoEmail: (selectedSlots: TimeSlot[]) => void;
}

export const ResultsList: React.FC<ResultsListProps> = ({
  slots,
  timeZone,
  attendees,
  includeAttendees,
  onInsertIntoEmail,
}) => {
  const [selectedSlots, setSelectedSlots] = useState<Set<number>>(new Set());
  const [copyMessage, setCopyMessage] = useState<string>('');

  const handleToggleSlot = (index: number) => {
    const newSelected = new Set(selectedSlots);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSlots(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSlots.size === slots.length) {
      setSelectedSlots(new Set());
    } else {
      setSelectedSlots(new Set(slots.map((_, i) => i)));
    }
  };

  const handleInsert = () => {
    const selected = slots.filter((_, i) => selectedSlots.has(i));
    onInsertIntoEmail(selected);
  };

  const handleCopy = async () => {
    const selected = slots.filter((_, i) => selectedSlots.has(i));

    const plainText = formatTimeSlotsForEmail(
      selected,
      timeZone,
      getTimezoneAbbreviation(timeZone),
      includeAttendees ? attendees : undefined,
      false // Plain text
    );

    // Try multiple clipboard methods for best compatibility
    let copied = false;

    // Method 1: Modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(plainText);
        copied = true;
      } catch (error) {
        console.log('Clipboard API failed, trying fallback:', error);
      }
    }

    // Method 2: execCommand fallback (works in more contexts)
    if (!copied) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = plainText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        copied = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (error) {
        console.error('execCommand fallback failed:', error);
      }
    }

    if (copied) {
      setCopyMessage('Copied to clipboard!');
    } else {
      setCopyMessage('Failed to copy - try Insert into Email instead');
    }
    setTimeout(() => setCopyMessage(''), 3000);
  };

  if (slots.length === 0) {
    return null;
  }

  const tzAbbrev = getTimezoneAbbreviation(timeZone);
  const tzFullName = getTimezoneFullName(timeZone);

  return (
    <div className="results-list">
      <div className="results-header">
        <label className="section-label">Available Times - {tzFullName} ({tzAbbrev})</label>
        <button className="link-button" onClick={handleSelectAll}>
          {selectedSlots.size === slots.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      <div className="slots-container">
        {slots.map((slot, index) => {
          // Re-format times in the recipient's timezone
          const displayStart = formatTime(slot.start, timeZone);
          const displayEnd = formatTime(slot.end, timeZone);
          const dateLabel = formatDateLabel(slot.start, timeZone);

          return (
            <div key={index} className="slot-item">
              <input
                type="checkbox"
                id={`slot-${index}`}
                checked={selectedSlots.has(index)}
                onChange={() => handleToggleSlot(index)}
                className="slot-checkbox"
              />
              <label htmlFor={`slot-${index}`} className="slot-label">
                <span className="slot-date">{dateLabel}:</span>
                <span className="slot-time">
                  {displayStart}–{displayEnd}
                </span>
              </label>
            </div>
          );
        })}
      </div>

      <div className="action-buttons">
        <button
          className="primary-btn"
          onClick={handleInsert}
          disabled={selectedSlots.size === 0}
        >
          Insert into Email
        </button>
        <button
          className="secondary-btn"
          onClick={handleCopy}
          disabled={selectedSlots.size === 0}
        >
          Copy
        </button>
      </div>

      {copyMessage && (
        <div className="copy-message">{copyMessage}</div>
      )}

      {selectedSlots.size === 0 && slots.length > 0 && !copyMessage && (
        <div className="hint-text">Select one or more times to insert</div>
      )}
    </div>
  );
};
